import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trophy } from 'lucide-react';

import ThemeToggle from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/AuthContext';

const RANK_STYLES = [
  'bg-amber-100 text-amber-700',
  'bg-slate-200 text-slate-600',
  'bg-orange-100 text-orange-700',
];

const TABS = [
  { key: 'all', label: 'All', track: null },
  { key: 'report', label: 'Personalized', track: 'report' },
  { key: 'general', label: 'General', track: 'general' },
  { key: 'monthly', label: 'Monthly', track: 'monthly' },
  { key: 'kb', label: 'Knowledge Base', track: 'kb' },
];

const TRACK_LABEL = {
  report: 'Personalized',
  general: 'General',
  monthly: 'Monthly',
  kb: 'Knowledge Base',
};

const trackBadge = (track) =>
  track === 'general' || track === 'monthly'
    ? 'bg-accent/10 text-accent'
    : 'bg-primary/10 text-primary';

// Deliberately no "4/5 correct": the percentage is the result. Showing the raw
// count reveals how long each quiz is and invites working out who missed what.
function Row({ entry, rank, dimmed = false }) {
  return (
    <li
      className={cn(
        'flex items-center gap-4 px-4 sm:px-5 py-3.5 rounded-xl border transition-colors',
        entry.is_me ? 'border-primary bg-primary/5' : 'border-border bg-card',
        dimmed && 'opacity-70',
      )}
    >
      <span
        className={cn(
          'flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold',
          rank <= 3 && !dimmed
            ? RANK_STYLES[rank - 1]
            : 'bg-muted text-muted-foreground',
        )}
      >
        {rank}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-foreground truncate">
            {entry.agent_name}
          </p>
          {entry.is_me && (
            <span className="text-xs font-semibold text-primary">You</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span
            className={cn(
              'inline-flex items-center text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded',
              trackBadge(entry.track),
            )}
          >
            {TRACK_LABEL[entry.track] || entry.track}
          </span>
          {entry.achieved_at && (
            <span className="text-xs text-muted-foreground">
              {new Date(entry.achieved_at).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      <span
        className={cn(
          'flex-shrink-0 text-lg font-semibold tabular-nums',
          dimmed ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {Math.round(entry.best_score)}%
      </span>
    </li>
  );
}

export default function Leaderboard() {
  const { isAgent } = useAuth();
  const [entries, setEntries] = useState(null);
  const [myBest, setMyBest] = useState(null);
  const [tab, setTab] = useState('all');
  const [showLow, setShowLow] = useState(false);

  useEffect(() => {
    let active = true;
    setEntries(null);

    const track = TABS.find((t) => t.key === tab)?.track ?? null;

    // Each agent's best PUBLISHED result. The function runs as SECURITY
    // DEFINER because RLS stops users reading each other's score rows; only
    // this aggregate crosses that line, and only for results people chose
    // to publish.
    supabase
      .rpc('get_leaderboard', { p_track: track, p_limit: 100 })
      .then(({ data, error }) => {
        if (!active) return;
        if (error) {
          console.error('Leaderboard failed:', error.message);
          setEntries([]);
          return;
        }
        setEntries(data ?? []);
      });

    supabase.rpc('get_my_best', { p_track: track }).then(({ data }) => {
      if (active) setMyBest(data?.[0] ?? null);
    });

    return () => {
      active = false;
    };
  }, [tab]);

  const loading = entries === null;
  const list = entries || [];
  const topScores = list.filter((s) => Number(s.best_score) >= 50);
  const lowScores = list.filter((s) => Number(s.best_score) < 50);

  // Show the "your result" card only when the person is not already listed,
  // either because they are a guest or because they kept their scores private.
  const meInList = list.some((e) => e.is_me);
  const showMine = myBest && !meInList;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="mr-1.5 w-4 h-4" />
            Back to quiz
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold tracking-tight text-foreground">
              Leaderboard
            </span>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12 sm:py-16">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 text-primary mb-5">
            <Trophy className="w-8 h-8" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
            Training Leaderboard
          </h1>
          <p className="mt-2 text-muted-foreground">
            Results agents chose to share. Best score per person.
          </p>
        </div>

        <div className="flex justify-center mb-8">
          <div className="inline-flex flex-wrap justify-center p-1 rounded-full bg-muted/60 border border-border">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={cn(
                  'px-4 py-1.5 rounded-full text-sm font-medium transition-colors',
                  tab === t.key
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            Nothing shared in this track yet. Finish a session and choose to add
            it here.
          </div>
        ) : (
          <>
            <ol className="space-y-2.5">
              {topScores.map((entry, i) => (
                <Row key={entry.user_id} entry={entry} rank={i + 1} />
              ))}
            </ol>

            {lowScores.length > 0 && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setShowLow((v) => !v)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
                >
                  {showLow ? 'Hide' : 'Show'} {lowScores.length} below 50%
                  <span className="text-xs">{showLow ? '▲' : '▼'}</span>
                </button>
                {showLow && (
                  <ol className="space-y-2.5 mt-2.5">
                    {lowScores.map((entry, i) => (
                      <Row
                        key={entry.user_id}
                        entry={entry}
                        rank={topScores.length + i + 1}
                        dimmed
                      />
                    ))}
                  </ol>
                )}
              </div>
            )}
          </>
        )}

        {showMine && (
          <div className="mt-8">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2.5">
              Your best
            </p>
            <div className="flex items-center gap-4 px-4 sm:px-5 py-3.5 rounded-xl border border-primary/40 bg-primary/5">
              <span className="flex-shrink-0 w-9 h-9 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs font-semibold">
                —
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">
                  You{' '}
                  <span className="text-xs font-normal text-muted-foreground">
                    ({isAgent ? 'not shared' : 'guest — not ranked'})
                  </span>
                </p>
                {myBest.achieved_at && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {new Date(myBest.achieved_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              <span className="flex-shrink-0 text-lg font-semibold text-foreground tabular-nums">
                {Math.round(myBest.best_score)}%
              </span>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {isAgent
                ? 'Only you can see this. Results are added to the board from the session results screen.'
                : 'The leaderboard ranks the customer care team. Ask an admin if you should be on it.'}
            </p>
          </div>
        )}

        <div className="mt-10 text-center">
          <Link to="/">
            <Button variant="outline" className="rounded-full border-border px-8">
              Take a training session
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
